import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import {
  Users,
  Envelope,
  Phone,
  Plus,
  PaperPlaneTilt,
  Eye,
  Copy,
  CheckCircle,
  X,
  SpinnerGap,
  Link as LinkIcon,
  Clock,
  ShieldCheck
} from '@phosphor-icons/react';
import { toast } from '../hooks/use-toast';

const API = process.env.REACT_APP_BACKEND_URL;

const ClientInviteManager = ({ jobId, jobTitle }) => {
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showInviteForm, setShowInviteForm] = useState(false);
  const [sending, setSending] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  
  // Form state
  const [clientName, setClientName] = useState('');
  const [clientEmail, setClientEmail] = useState('');
  const [clientPhone, setClientPhone] = useState('');

  useEffect(() => {
    fetchInvites();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional mount-only effect
  }, [jobId]);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  });

  const fetchInvites = async () => {
    try {
      const response = await axios.get(
        `${API}/api/client-portal/invites/job/${jobId}`,
        getAuthHeaders()
      );
      setInvites(response.data.invites || []);
    } catch (error) {
      console.error('Failed to fetch invites:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    
    if (!clientName || !clientEmail) {
      toast({ title: 'Please fill in name and email', variant: 'destructive' });
      return;
    }
    
    setSending(true);
    try {
      const response = await axios.post(
        `${API}/api/client-portal/invite`,
        {
          job_id: jobId,
          client_name: clientName,
          client_email: clientEmail,
          client_phone: clientPhone || null
        },
        getAuthHeaders()
      );
      
      toast({
        title: 'Invite Sent!',
        description: `${clientName} will receive an email with portal access.`
      });
      
      setShowInviteForm(false);
      setClientName('');
      setClientEmail('');
      setClientPhone('');
      fetchInvites();
    } catch (error) {
      toast({
        title: 'Failed to send invite',
        description: error.response?.data?.detail || 'Please try again',
        variant: 'destructive'
      });
    } finally {
      setSending(false);
    }
  };

  const handleResendInvite = async (inviteId) => {
    try {
      await axios.post(
        `${API}/api/client-portal/invite/${inviteId}/resend`,
        {},
        getAuthHeaders()
      );
      toast({ title: 'Invite resent successfully' });
    } catch (error) {
      toast({ title: 'Failed to resend', variant: 'destructive' });
    }
  };

  const handleToggleInvite = async (inviteId, isActive) => {
    try {
      await axios.patch(
        `${API}/api/client-portal/invite/${inviteId}`,
        { is_active: !isActive },
        getAuthHeaders()
      );
      toast({ title: isActive ? 'Access revoked' : 'Access restored' });
      fetchInvites();
    } catch (error) {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  const copyInviteLink = async (token_preview, inviteId) => {
    // Since we don't have the full token, we'll just show the preview
    // In production, you might want to generate a shareable link
    try {
      const baseUrl = window.location.origin;
      // Note: token_preview is shortened, so we indicate this is for reference
      await navigator.clipboard.writeText(`${baseUrl}/client-portal (Check email for full link)`);
      setCopiedId(inviteId);
      setTimeout(() => setCopiedId(null), 2000);
      toast({ title: 'Link hint copied', description: 'Full link was sent to client email' });
    } catch {
      toast({ title: 'Failed to copy', variant: 'destructive' });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <SpinnerGap size={32} className="animate-spin text-[#2B4C6F]" />
      </div>
    );
  }

  return (
    <div className="bg-white dark:bg-neutral-900 border-2 border-black dark:border-neutral-700 rounded-xl shadow-[4px_4px_0px_#000] dark:shadow-[4px_4px_0px_#2B4C6F] overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#2B4C6F] to-[#203A55] text-white p-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Users size={28} weight="bold" />
          <div>
            <h3 className="font-bold text-lg">Client Portal Access</h3>
            <p className="text-sm text-white/80">Invite homeowners to view progress</p>
          </div>
        </div>
        
        <Button
          onClick={() => setShowInviteForm(true)}
          className="bg-white text-[#2B4C6F] hover:bg-gray-100 border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none font-bold"
          data-testid="invite-client-btn"
        >
          <Plus size={20} weight="bold" className="mr-2" />
          Invite Client
        </Button>
      </div>

      {/* Content */}
      <div className="p-4">
        {invites.length === 0 ? (
          <div className="text-center py-8 text-gray-500 dark:text-gray-400">
            <ShieldCheck size={48} className="mx-auto mb-3 opacity-50" />
            <p className="font-medium">No clients invited yet</p>
            <p className="text-sm mt-1">Invite homeowners to track their project progress</p>
          </div>
        ) : (
          <div className="space-y-3">
            {invites.map((invite) => (
              <div 
                key={invite._id}
                className={`border-2 rounded-lg p-4 ${
                  invite.is_active 
                    ? 'border-green-300 bg-green-50 dark:bg-green-900/20' 
                    : 'border-neutral-300 bg-neutral-50 dark:bg-neutral-800'
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <p className="font-bold text-lg">{invite.client_name}</p>
                      <Badge className={invite.is_active ? 'bg-green-500' : 'bg-neutral-400'}>
                        {invite.is_active ? 'Active' : 'Revoked'}
                      </Badge>
                      {invite.has_account && (
                        <Badge variant="outline" className="border-blue-400 text-blue-600">
                          Has Account
                        </Badge>
                      )}
                    </div>
                    
                    <div className="flex flex-wrap gap-4 mt-2 text-sm text-neutral-600 dark:text-neutral-400">
                      <span className="flex items-center gap-1">
                        <Envelope size={14} />
                        {invite.client_email}
                      </span>
                      {invite.client_phone && (
                        <span className="flex items-center gap-1">
                          <Phone size={14} />
                          {invite.client_phone}
                        </span>
                      )}
                      {invite.last_accessed && (
                        <span className="flex items-center gap-1">
                          <Eye size={14} />
                          Last viewed: {new Date(invite.last_accessed).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyInviteLink(invite.token_preview, invite._id)}
                      className="h-8"
                      title="Copy invite link info"
                    >
                      {copiedId === invite._id ? (
                        <CheckCircle size={18} className="text-green-600" />
                      ) : (
                        <Copy size={18} />
                      )}
                    </Button>
                    
                    {invite.is_active && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleResendInvite(invite._id)}
                        className="h-8"
                        title="Resend invite email"
                      >
                        <PaperPlaneTilt size={18} />
                      </Button>
                    )}
                    
                    <Button
                      variant={invite.is_active ? 'destructive' : 'default'}
                      size="sm"
                      onClick={() => handleToggleInvite(invite._id, invite.is_active)}
                      className="h-8 text-xs"
                    >
                      {invite.is_active ? 'Revoke' : 'Restore'}
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Invite Form Modal */}
      {showInviteForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div 
            className="bg-white dark:bg-neutral-900 rounded-xl border-2 border-black dark:border-neutral-700 shadow-[6px_6px_0px_#000] w-full max-w-md"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-[#2B4C6F] text-white p-4 rounded-t-lg flex items-center justify-between">
              <h3 className="font-bold text-lg">Invite Client to Portal</h3>
              <button onClick={() => setShowInviteForm(false)} className="hover:bg-white/20 p-1 rounded">
                <X size={20} />
              </button>
            </div>
            
            <form onSubmit={handleSendInvite} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-bold mb-1">Client Name *</label>
                <Input
                  value={clientName}
                  onChange={(e) => setClientName(e.target.value)}
                  placeholder="John Smith"
                  className="border-2 border-black dark:border-neutral-700"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold mb-1">Email Address *</label>
                <Input
                  type="email"
                  value={clientEmail}
                  onChange={(e) => setClientEmail(e.target.value)}
                  placeholder="client@email.com"
                  className="border-2 border-black dark:border-neutral-700"
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-bold mb-1">Phone Number (for SMS alerts)</label>
                <Input
                  type="tel"
                  value={clientPhone}
                  onChange={(e) => setClientPhone(e.target.value)}
                  placeholder="+1 (555) 000-0000"
                  className="border-2 border-black dark:border-neutral-700"
                />
              </div>
              
              <div className="bg-blue-50 dark:bg-blue-900/30 p-3 rounded-lg border-2 border-blue-200 dark:border-blue-800">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  <strong>What they'll see:</strong> Project status, photos, documents, 
                  milestones, cost breakdown, and can message you directly.
                </p>
              </div>
              
              <div className="flex gap-3 pt-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setShowInviteForm(false)}
                  className="flex-1 border-2 border-black dark:border-neutral-700"
                  disabled={sending}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={sending}
                  className="flex-1 bg-[#2B4C6F] hover:bg-[#203A55] text-white border-2 border-black shadow-[2px_2px_0px_#000] active:translate-y-0.5 active:shadow-none font-bold"
                  data-testid="send-invite-btn"
                >
                  {sending ? (
                    <><SpinnerGap size={20} className="animate-spin mr-2" /> Sending...</>
                  ) : (
                    <><PaperPlaneTilt size={20} className="mr-2" /> Send Invite</>
                  )}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientInviteManager;
